import React from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { Accordion, AccordionDetails, AccordionSummary, Button, Container, Stack, Typography } from '@mui/material';
import { ApiError } from 'api';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { commonMessages } from 'utils/commonMessages';

interface Props {
  title: string;
  message: string;
  svgImage: JSX.Element;
  error?: ApiError;
}

export const ErrorPage: React.FC<Props> = ({
  title,
  message,
  svgImage,
  error
}) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const handleHomeButton = () => navigate('/');
  const handleBackButton = () => navigate(-1);

  return (
    <Stack justifyContent="center" flexGrow={1} gap={2}>
      <Container>
        <Stack alignItems="center" gap={7}>
          {svgImage}
          <Stack gap={2}>
            <Typography variant="h4" align="center">
              {title}
            </Typography>
            <Typography align="center">
              {message}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-around"
            alignItems="center"
            columnGap={2}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={handleBackButton}
            >
              {formatMessage(commonMessages.back)}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleHomeButton}
            >
              {formatMessage(commonMessages.home)}
            </Button>
          </Stack>
        </Stack>
      </Container>
      {error && (
        <Container sx={{py: 4}}>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography variant='subtitle2' color='primary'>{formatMessage(commonMessages.showMore)}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant='h6'>{error.title}</Typography>
              <Typography variant='subtitle1' color='text.secondary'>{error.detail}</Typography>
              {error.exceptionDetails && error.exceptionDetails.map((exceptionDetail, index) => (
                <SyntaxHighlighter key={index} language='csharp' style={dracula} wrapLines showLineNumbers>
                  {exceptionDetail.raw}
                </SyntaxHighlighter>
              ))}
            </AccordionDetails>
          </Accordion>
        </Container>
      )}
    </Stack>
  );
};
